/**
 * ProjectSummary — issue #821.
 *
 * Per-project truth-only dashboard rendered at the top of ProjectPage.
 * Every number traces to a real source; if a data plane isn't
 * configured (no analysis, mock operational adapter, mock security
 * adapter), the section is absent — replaced by a compact one-line
 * CTA explaining what the user can connect. No synthesised figures.
 *
 * Evidence-linked: every tile is a clickable `<Link>` that navigates
 * to the relevant drill-down page (stitch review, flows, call graph,
 * endpoints) so the dashboard is an entry point, not a dead end.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import { ClickStitchReviewCard } from '../stitching/ClickStitchReviewEntry';
import type {
  StructuralSummaryPayload,
  HotspotRow,
  OrphanRow,
  HotEndpointRow,
  DistributionSegment,
} from '@veodiagram/shared';

interface ProjectSummaryProps {
  projectId: string;
}

export function ProjectSummary({ projectId }: ProjectSummaryProps) {
  const [structural, setStructural] = useState<StructuralSummaryPayload | null>(null);
  const [loading, setLoading] = useState(true);

  // Issue #860: accept a `refresh` flag to bypass server-side caches.
  // #1074 E3: only fetch structural summary in v1.0 — Operational and Security
  // panels are now "Coming next release" placeholders (E1+E2). The old
  // per-project ops/sec sub-tiles were producing 500s on stale DBs without
  // the DashboardSummary migration, and their content was synthesised mock
  // anyway. AdapterCTAs below render unconditionally in their place.
  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    try {
      const s = await api
        .getStructuralSummary({ projectId, refresh })
        .catch(() => null);
      setStructural(s);
      // If structural came back all-zero, auto-retry once after 3s
      // (analysis may still be writing diagrams).
      if (!refresh && s && s.tiles.userExperiences === 0 && s.tiles.apiEndpoints === 0) {
        setTimeout(() => void load(true), 3000);
      }
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div
        data-testid="project-summary"
        className="mb-8 p-4 border border-border rounded text-sm text-muted-foreground"
      >
        Loading project summary…
      </div>
    );
  }

  // If structural data is empty or missing, the project hasn't been analyzed.
  // A project is "not analyzed" when the structural endpoint returns
  // zero projects in scope. We don't check userExperiences === 0
  // because a pure API project can legitimately have 0 interactions
  // but many endpoints — that project IS analyzed and should show
  // its structural metrics.
  const notAnalyzed = !structural || structural.projectCount === 0;

  if (notAnalyzed) {
    return (
      <div
        data-testid="project-summary"
        data-state="not-analyzed"
        className="mb-8 p-5 border border-border rounded bg-muted/30"
      >
        <div className="text-sm font-semibold text-foreground mb-1">
          No analysis data yet
        </div>
        <p className="text-xs text-muted-foreground">
          Run the analyzer on this project to see structural metrics,
          hotspots, dead code, and stitch coverage. Operational and
          security summaries become available once you connect real
          monitoring and scanning tools.
        </p>
      </div>
    );
  }

  const t = structural.tiles;
  const stitchesToReview = t.ambiguousStitches + t.unresolvedStitches;

  return (
    <div data-testid="project-summary" className="mb-8 space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          Project summary
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 uppercase tracking-wide">
            live
          </span>
        </h2>
        <button
          data-testid="project-summary-refresh"
          onClick={() => void load(true)}
          className="text-xs px-2 py-1 rounded border border-border hover:bg-muted"
        >
          Refresh
        </button>
      </div>

      {/* ─── #1382 item 6 — Discovery health summary line ───
           One-line framing of what the analyzer found, anchored to the
           tile counts. Builds user confidence at first glance ("the
           analyzer detected N routes + M user experiences in this
           project") before they click anywhere.
           Empty-detection short-circuit (PR #1387 review #1): an
           "analyzed-but-found-nothing" project would otherwise render
           "Detected 0 routes + 0 user experiences" — replace with an
           explicit message that names the likely fix. */}
      {t.apiEndpoints === 0 && t.userExperiences === 0 ? (
        <div
          data-testid="project-discovery-health"
          className="text-sm text-muted-foreground"
        >
          Analyzer detected no routes or user experiences in this project —
          check that the source root contains the app code.
        </div>
      ) : (
        <div
          data-testid="project-discovery-health"
          className="text-sm text-muted-foreground"
        >
          Detected{' '}
          <span className="font-mono font-medium text-foreground">
            {t.apiEndpoints} routes
          </span>
          {' '}+{' '}
          <span className="font-mono font-medium text-foreground">
            {t.userExperiences} user experiences
          </span>
          {' '}stitched into{' '}
          <span className="font-mono font-medium text-foreground">
            {t.unambiguousE2EFlows} clean E2E flows
          </span>
          {t.ambiguousStitches + t.unresolvedStitches > 0 && (
            <>
              {' '}with{' '}
              <Link
                to={`/projects/${projectId}/stitch-review`}
                className="font-mono font-medium text-amber-700 dark:text-amber-300 hover:underline"
              >
                {t.ambiguousStitches + t.unresolvedStitches} stitches needing review
              </Link>
            </>
          )}
          .
        </div>
      )}

      {/* ─── Executive tiles (evidence-linked) ─── */}
      <div
        data-testid="project-tiles"
        className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3"
      >
        <TileLink
          testId="ptile-ux"
          label="User experiences"
          value={t.userExperiences}
          to={`/projects/${projectId}/flows`}
          tone="blue"
        />
        <TileLink
          testId="ptile-endpoints"
          label="API endpoints"
          value={t.apiEndpoints}
          to={`/projects/${projectId}/endpoints`}
          tone="blue"
        />
        <TileLink
          testId="ptile-clean-flows"
          label="Clean E2E flows"
          value={t.unambiguousE2EFlows}
          to={`/projects/${projectId}/flows?filter=clean`}
          tone="green"
        />
        <TileLink
          testId="ptile-ambiguous"
          label="Ambiguous stitches"
          value={t.ambiguousStitches}
          to={`/projects/${projectId}/stitch-review`}
          tone={t.ambiguousStitches > 0 ? 'amber' : 'green'}
        />
        <TileLink
          testId="ptile-unresolved"
          label="Unresolved stitches"
          value={t.unresolvedStitches}
          to={`/projects/${projectId}/stitch-review`}
          tone={t.unresolvedStitches > 0 ? 'red' : 'green'}
        />
        <TileLink
          testId="ptile-evidence"
          label="Evidence coverage"
          value={`${Math.round(t.evidenceCoverage * 100)}%`}
          to={`/projects/${projectId}/call-graph`}
          tone="slate"
        />
      </div>

      {/* ─── Stitch review CTAs (#963 track 3: peer-level discoverability) ─── */}
      {/* URL stitch review (api-call → api-route, #812) and click stitch
          review (UI handler → api-call, #963) sit stacked at the same
          level. Both gated on their own counts so the strip is empty
          when everything is stitched (no orphan column when one is
          hidden). */}
      <div className="flex flex-col gap-2">
        {stitchesToReview > 0 && (
          <Link
            to={`/projects/${projectId}/stitch-review`}
            data-testid="project-summary-stitch-cta"
            className="flex items-center gap-2 px-4 py-2.5 border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 rounded hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
          >
            <svg
              className="w-4 h-4 text-amber-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
            <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
              {stitchesToReview} URL stitch{stitchesToReview === 1 ? '' : 'es'} to review
            </span>
            <span className="text-xs text-amber-700/70 dark:text-amber-300/70 ml-auto">
              {t.ambiguousStitches} ambiguous · {t.unresolvedStitches} unresolved
            </span>
          </Link>
        )}
        <ClickStitchReviewCard projectId={projectId} />
      </div>

      {/* ─── Health strip ─── */}
      <div data-testid="project-health-strip" className="space-y-2">
        {Object.entries(structural.healthStrip)
          .filter(([key, segments]) => !shouldHideStrip(key, segments))
          .map(([key, segments]) => (
            <MiniBar key={key} label={formatStripLabel(key)} segments={segments} />
          ))}
      </div>

      {/* ─── Hotspots + Orphans ─── */}
      {(structural.hotspots.length > 0 || structural.orphans.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {structural.hotspots.length > 0 && (
            <HotspotsMini projectId={projectId} rows={structural.hotspots} />
          )}
          {structural.orphans.length > 0 && (
            <OrphansMini projectId={projectId} rows={structural.orphans} />
          )}
        </div>
      )}

      {/* Issue #907: Hot endpoints — backend handlers ranked by FE
          stitch fan-in. Distinct from Hotspots above (in-process AST).
          Only renders when at least one endpoint has a stitch. */}
      {structural.hotEndpoints && structural.hotEndpoints.length > 0 && (
        <HotEndpointsMini projectId={projectId} rows={structural.hotEndpoints} />
      )}

      {/* ─── Operational CTA (#1074 E3 — Mini panel removed with v1.0
              dashboard placeholders) ─── */}
      <AdapterCTA
        testId="project-op-cta"
        label="Operational metrics"
        description="Connect Datadog, Dynatrace, Grafana, Prometheus, or New Relic to see live golden signals, SLO burn rates, and synthetic pass rates for this project."
      />

      {/* ─── Security CTA (#1074 E3 — Mini panel removed with v1.0
              dashboard placeholders) ─── */}
      <AdapterCTA
        testId="project-sec-cta"
        label="Security posture"
        description="Connect Snyk, Semgrep, Checkmarx, Orca, or a continuous pen-testing provider to see vulnerability findings, attack-surface analysis, and gateway risk scores."
      />
    </div>
  );
}

// ─────────────────────────── subcomponents ───────────────────────────

function TileLink({
  testId,
  label,
  value,
  to,
  tone,
}: {
  testId: string;
  label: string;
  value: number | string;
  to: string;
  tone: 'blue' | 'green' | 'amber' | 'red' | 'slate';
}) {
  const toneMap: Record<typeof tone, string> = {
    blue: 'text-blue-600',
    green: 'text-green-600',
    amber: 'text-amber-600',
    red: 'text-red-600',
    slate: 'text-slate-600 dark:text-slate-300',
  };
  return (
    <Link
      to={to}
      data-testid={testId}
      aria-label={`${label}: ${value}`}
      className="p-3 bg-muted/40 border border-border rounded hover:border-primary hover:shadow-sm transition-all group"
    >
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div
        className={`text-xl font-semibold mt-0.5 ${toneMap[tone]} group-hover:underline`}
      >
        {value}
      </div>
    </Link>
  );
}

function MiniBar({
  label,
  segments,
}: {
  label: string;
  segments: DistributionSegment[];
}) {
  const total = segments.reduce((s, x) => s + x.value, 0);
  if (total === 0) return null;
  const toneMap: Record<string, string> = {
    green: 'bg-green-500',
    amber: 'bg-amber-500',
    red: 'bg-red-500',
    blue: 'bg-blue-500',
    purple: 'bg-purple-500',
    slate: 'bg-slate-400',
  };
  const cycle = ['bg-blue-500', 'bg-indigo-500', 'bg-sky-500', 'bg-cyan-500'];
  // Issue #904: every non-zero segment gets a stable colour shared by the
  // bar fill and the legend chip, so users can match colour↔value at a glance.
  const segmentColors = segments.map(
    (seg, i) => (seg.tone && toneMap[seg.tone]) ?? cycle[i % cycle.length],
  );
  const visibleSegments = segments
    .map((seg, i) => ({ seg, color: segmentColors[i] }))
    .filter(({ seg }) => seg.value > 0);
  return (
    <div className="text-xs">
      <div className="flex items-center gap-2">
        <span className="w-52 shrink-0 text-muted-foreground truncate">
          {label}
        </span>
        <div className="flex-1 h-3 rounded overflow-hidden border border-border flex">
          {segments.map((seg, i) => {
            const w = (seg.value / total) * 100;
            return (
              <div
                key={`${seg.label}-${i}`}
                className={segmentColors[i]}
                style={{ width: `${w}%` }}
                title={`${seg.label}: ${seg.value}`}
              />
            );
          })}
        </div>
        <span className="w-10 text-right text-muted-foreground font-mono">
          {total}
        </span>
      </div>
      {/* Issue #904: always-visible per-segment legend so users don't have
          to hover a 12px bar to read the breakdown. */}
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 ml-[calc(13rem+0.5rem)] text-[11px] text-muted-foreground">
        {visibleSegments.map(({ seg, color }, i) => (
          <span key={`${seg.label}-legend-${i}`} className="inline-flex items-center gap-1">
            <span className={`inline-block w-2 h-2 rounded-sm ${color}`} aria-hidden />
            <span>
              {seg.label}: <span className="font-mono">{seg.value}</span>
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

function HotspotsMini({
  projectId,
  rows,
}: {
  projectId: string;
  rows: HotspotRow[];
}) {
  // Issue #941: default-collapsed — these tables eat a lot of vertical
  // space and aren't first-look content. Use native <details> with the
  // header styled to match the rest of the dashboard's section bars and
  // a chevron that rotates on open.
  return (
    <details
      data-testid="project-hotspots"
      className="group border border-border rounded"
    >
      <summary className="px-3 py-1.5 border-b border-transparent group-open:border-border bg-muted/30 text-[11px] font-semibold uppercase text-muted-foreground cursor-pointer flex items-center justify-between list-none [&::-webkit-details-marker]:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-t">
        <span>Hotspots — most called ({rows.length})</span>
        <svg
          className="w-3 h-3 transition-transform group-open:rotate-90"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </summary>
      <ul className="divide-y divide-border max-h-48 overflow-auto">
        {rows.slice(0, 8).map(h => (
          <li key={h.functionId} className="px-2 py-1.5 text-xs">
            <Link
              to={`/projects/${projectId}/call-graph`}
              className="hover:text-primary transition-colors"
              data-testid="hotspot-link"
            >
              <span className="font-mono truncate block">{h.functionName}</span>
              <span className="text-muted-foreground">
                {h.callerCount} callers
                {h.filePath && ` · ${shortenPath(h.filePath)}`}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </details>
  );
}

/**
 * Issue #907: Hot endpoints — backend handlers ranked by stitched FE
 * call sites. Each row carries the method + path + handler with two
 * caller-count buckets (confident / ambiguous) and an optional auth
 * indicator. Distinct from HotspotsMini above (in-process AST fan-in)
 * — this panel surfaces "which API endpoints are users actually
 * exercising" using the auto-stitcher's HTTP-layer evidence.
 */
function HotEndpointsMini({
  projectId,
  rows,
}: {
  projectId: string;
  rows: HotEndpointRow[];
}) {
  return (
    <div
      data-testid="project-hot-endpoints"
      className="border border-border rounded"
    >
      <div className="px-3 py-1.5 border-b border-border bg-muted/30 text-[11px] font-semibold uppercase text-muted-foreground flex items-center justify-between">
        <span>Hot endpoints — most called from FE</span>
        <span className="text-[10px] text-muted-foreground/70 normal-case">
          via stitches
        </span>
      </div>
      <ul className="divide-y divide-border max-h-48 overflow-auto">
        {rows.slice(0, 8).map(h => (
          <li key={h.endpointId} className="px-2 py-1.5 text-xs">
            <Link
              to={`/projects/${projectId}/backend-graph`}
              className="hover:text-primary transition-colors"
              data-testid="hot-endpoint-link"
            >
              <div className="flex items-center gap-1.5">
                <span
                  className={`inline-flex items-center px-1 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
                    h.method === 'GET'
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                      : h.method === 'POST'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                      : h.method === 'PUT' || h.method === 'PATCH'
                      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                      : h.method === 'DELETE'
                      ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                      : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                  }`}
                >
                  {h.method}
                </span>
                <span className="font-mono truncate flex-1">{h.path}</span>
                {h.requiresAuth === false && (
                  <span
                    className="inline-flex items-center px-1 py-0.5 rounded text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                    title="Endpoint does not require authentication"
                  >
                    no auth
                  </span>
                )}
              </div>
              <div className="text-muted-foreground mt-0.5">
                <span className="font-mono">{h.callerCount}</span>{' '}
                {h.callerCount === 1 ? 'caller' : 'callers'}
                {h.ambiguousCallerCount > 0 && (
                  <span className="text-amber-600 dark:text-amber-400">
                    {' '}· {h.ambiguousCallerCount} ambiguous
                  </span>
                )}
                {h.handlerName && ` · ${h.handlerName}`}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function OrphansMini({
  projectId,
  rows,
}: {
  projectId: string;
  rows: OrphanRow[];
}) {
  // Issue #941: default-collapsed (same rationale as HotspotsMini above).
  return (
    <details
      data-testid="project-orphans"
      className="group border border-border rounded"
    >
      <summary className="px-3 py-1.5 border-b border-transparent group-open:border-border bg-muted/30 text-[11px] font-semibold uppercase text-muted-foreground cursor-pointer flex items-center justify-between list-none [&::-webkit-details-marker]:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-t">
        <span>Unused code — dead zones ({rows.length})</span>
        <svg
          className="w-3 h-3 transition-transform group-open:rotate-90"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </summary>
      <ul className="divide-y divide-border max-h-48 overflow-auto">
        {rows.slice(0, 8).map(o => (
          <li key={o.functionId} className="px-2 py-1.5 text-xs">
            <Link
              to={`/projects/${projectId}/call-graph`}
              className="hover:text-primary transition-colors"
              data-testid="orphan-link"
            >
              <span className="font-mono truncate block">{o.functionName}</span>
              {o.filePath && (
                <span className="text-muted-foreground">
                  {shortenPath(o.filePath)}
                </span>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </details>
  );
}

// #1074 E3: `OperationalMini`, `SecurityMini`, and their inner `MiniTile`
// helper all removed — the parent panels are now "Coming next release"
// placeholders and the per-project tiles always render the `AdapterCTA`
// fallback.

function AdapterCTA({
  testId,
  label,
  description,
}: {
  testId: string;
  label: string;
  description: string;
}) {
  return (
    <div
      data-testid={testId}
      role="status"
      className="flex items-start gap-3 px-4 py-3 border border-dashed border-border rounded text-xs text-muted-foreground"
    >
      <svg
        className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground/60"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z"
        />
      </svg>
      <div>
        <span className="font-semibold text-foreground">{label}:</span>{' '}
        {description}
      </div>
    </div>
  );
}

function formatStripLabel(key: string): string {
  // Issue #903: label names the population the bar's right-hand total counts,
  // not the bucketing dimension — so e.g. "Endpoints by framework: 64" reads
  // as "64 endpoints" rather than "64 frameworks".
  const map: Record<string, string> = {
    stitchConfidence: 'Stitches by confidence',
    endpointAuth: 'Endpoints by auth',
    frameworks: 'Endpoints by framework',
    complexity: 'Functions by complexity',
    dynamicEndpoints: 'API call sites by resolution',
  };
  return map[key] ?? key;
}

/**
 * Issue #903: skip bars where the breakdown is degenerate and the panel
 * would mislead — a 100%-Unknown auth bar reads as "all endpoints
 * unprotected" when it really means auth wasn't classified, and a
 * one-segment Frameworks bar tells the user nothing the auth bar didn't.
 */
function shouldHideStrip(key: string, segments: DistributionSegment[]): boolean {
  const nonZero = segments.filter((s) => s.value > 0);
  if (nonZero.length === 0) return true;
  if (key === 'endpointAuth') {
    return nonZero.length === 1 && nonZero[0].label === 'Unknown';
  }
  if (key === 'frameworks') {
    return nonZero.length <= 1;
  }
  return false;
}

function shortenPath(p: string): string {
  const parts = p.split('/');
  return parts.slice(-3).join('/');
}
